import * as React from 'react';
import Avatar from '@mui/joy/Avatar';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import CircleIcon from '@mui/icons-material/Circle';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DangerousIcon from '@mui/icons-material/Dangerous';
import LogoutIcon from '@mui/icons-material/Logout';

import { openMessagesPane, toggleMessagesPane } from '../../utils';
// import WorkIndicator from '../WorkIndicator/WorkIndicator';
import { APIGetProfilesNotInChat, APIAddUserToChat, APIToggleChatDanger, APILeaveChat, APIGetChatResponseStats } from '../../api';
import { LayoutContext } from '../../context';
import { canSeeDangerMarks } from '../../utils/dangerVisibility';
import { useContext } from 'react';
import { useState } from 'react';
import { AvatarGroup, Box, Dropdown, Input, List, ListItem, ListItemButton, Menu, MenuButton, MenuItem, Sheet, Tooltip } from '@mui/joy';
import { useEffect } from 'react';
import { formatResponseDuration } from './ChatBubble';


// Возвращает данные собеседника-врача для ЛС (default-чат, 1 собеседник, тип doctor).
// Если условие не выполнено или у врача не задано response_time_hours — вернёт null.
function getDoctorForDM(chat) {
    if (!chat || chat.type_chat !== 'default') return null;
    const others = chat.participants || [];
    if (others.length !== 1) return null;
    const peer = others[0];
    if (!peer?.type_profile?.includes('doctor')) return null;
    if (peer.response_time_hours == null) return null;
    return peer;
}

// Грубое склонение «часов»
function pluralHours(n) {
    const mod10 = n % 10, mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'часа';
    if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'часов';
    return 'часов';
}


export default function MessagesPaneHeader({ chat, wsConnected, setSelectedChat, searchParams, setSearchParams, onUserAdded, setChats, onCloseChat }) {
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [responseStats, setResponseStats] = useState(null);
  const { token, account, loadChats, isPatient } = useContext(LayoutContext);
  const showDanger = canSeeDangerMarks(account) && !isPatient;

  // Подгружаем статистику ответов в чате (для сотрудников)
  useEffect(() => {
    let cancelled = false;
    if (!chat?.chat_id || isPatient) {
      setResponseStats(null);
      return;
    }
    APIGetChatResponseStats(chat.chat_id, token)
      .then(data => { if (!cancelled) setResponseStats(data); })
      .catch(() => { /* 403 если не участник + не админ — это нормально */ });
    return () => { cancelled = true; };
  }, [chat?.chat_id, token, isPatient]);

  // Проверяем, является ли пользователь участником чата
  useEffect(() => {
    if (chat && account) {
      // Проверяем, есть ли текущий пользователь в участниках чата
      const member = chat.is_member ?? !!chat.participants_with_roles?.some(
        participant => participant.profile.id === account.id
      );
      setIsMember(member);
    }
  }, [chat, account]);

  const handleLeaveChat = async () => {
    if (!chat?.chat_id || isLeaving) return;
    
    try {
      setIsLeaving(true);
      const response = await APILeaveChat(chat.chat_id, token);
      
      if (response.success) {
        alert(response.message);
        setIsMember(false);
        
        // Обновляем список чатов
        await loadChats();
        
        // Закрываем чат, так как пользователь больше не участник
        onCloseChat?.();
        setSelectedChat(null);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("chatId");
        setSearchParams(newParams);
      }
    } catch (error) {
      console.error('Ошибка при выходе из чата:', error);
      alert(error.response?.data?.error || 'Ошибка выхода из чата', { variant: 'error' });
    } finally {
      setIsLeaving(false);
    }
  };

  const handleToggleDanger = async () => {
    if (!showDanger) return;
    try {
      const response = await APIToggleChatDanger(chat.chat_id, token);
      if (response.success) {
        // Обновляем состояние списка чатов
        if (setChats) {
          setChats(prevChats => 
            prevChats.map(c => 
              c.chat_id === response.chat_id 
                ? { ...c, is_dangerous: response.is_dangerous }
                : c
            )
          );
        }

        // Обновляем локальное состояние чата если нужно
        if (setSelectedChat) {
          setSelectedChat(prev => ({
            ...prev,
            is_dangerous: response.is_dangerous
          }));
        }
        // Здесь можно обновить состояние в родительском компоненте
        console.log('Статус опасности чата обновлен:', response.is_dangerous);
      }
    } catch (error) {
      console.error('Ошибка при изменении статуса опасности:', error);
    }
  };

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0,
        py: { xs: 1, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface',
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 1, md: 2 }}
        sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}
      >
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          sx={{ display: onCloseChat ? 'none' : { xs: 'inline-flex', sm: 'none' } }}
          onClick={() => {
            setSelectedChat(null);
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("chatId");
            setSearchParams(newParams);
            openMessagesPane();
          }}
        >
          <ArrowBackIosNewRoundedIcon />
        </IconButton>
        <Avatar size="md" sx={{ flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              noWrap
              sx={{ fontWeight: 'lg', fontSize: { xs: 'sm', sm: 'md' }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {chat?.display_name || chat?.name}
            </Typography>
            
            {/* Индикатор опасного чата */}
            {chat?.is_dangerous && showDanger && (
              <Tooltip title="Опасный чат - содержит сообщения с тревожным содержанием" arrow>
                <Chip
                  variant="soft"
                  color="warning"
                  size="sm"
                  startDecorator={<WarningIcon sx={{ fontSize: 16 }} />}
                  onClick={handleToggleDanger}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'warning.200' }
                  }}
                >
                  Опасный
                </Chip>
              </Tooltip>
            )}
          </Stack>

          {account && !isPatient && (
            <Typography level="body-sm" color="neutral">
              ID чата: {chat?.chat_id}
              {chat.participants?.length > 1 && (
                <> • {chat.participants?.length} участников</>
              )}
            </Typography>
          )}
          
          {/* Информация о количестве опасных сообщений */}
          {chat?.danger_messages_count > 0 && showDanger && (
            <Typography level="body-xs" color="warning.600">
              {chat.danger_messages_count} опасных сообщений
            </Typography>
          )}

          {/* Среднее время ответа врача в этом чате — только сотрудникам */}
          {responseStats?.avg_response_seconds != null && account && !isPatient && (
            <Tooltip
              arrow
              title={
                <Box sx={{ p: 0.5 }}>
                  <div>Минимум: {formatResponseDuration(responseStats.min_response_seconds)}</div>
                  <div>Максимум: {formatResponseDuration(responseStats.max_response_seconds)}</div>
                  <div>Ответов врача: {responseStats.doctor_replies_count}</div>
                </Box>
              }
            >
              <Typography level="body-xs" color="neutral" sx={{ cursor: 'help' }}>
                ⏱ Среднее время ответа: {formatResponseDuration(responseStats.avg_response_seconds)}
              </Typography>
            </Tooltip>
          )}

          {/* Обычное время ответа врача — для ЛС с врачом, видно всем участникам */}
          {(() => {
              const doc = getDoctorForDM(chat);
              if (!doc) return null;
              return (
                  <Typography level="body-xs" color="primary" sx={{ fontStyle: 'italic' }}>
                      💬 Обычно отвечает в течение {doc.response_time_hours} {pluralHours(doc.response_time_hours)}
                  </Typography>
              );
          })()}

        </div>
      </Stack>

      
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
      { account && !isPatient && (
        <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
          <AddContactDropdown
            chat={chat}
            open={addContactOpen}
            onClose={() => setAddContactOpen(false)}
            setAddContactOpen={setAddContactOpen}
            onUserAdded={onUserAdded}
          />

          <Dropdown>
            <MenuButton
              size="sm" 
              color="neutral"
              variant={"outlined"}
              sx={{
                  borderRadius: "lg",
                  px: 0.8,
              }}
            >
              <MoreVertIcon />
            </MenuButton>

            <Menu size='sm' sx={{ zIndex: 10013 }}>
              {showDanger && (
                <MenuItem startDecorator={<DangerousIcon />} variant={chat?.is_dangerous ? "solid" : "soft"} onClick={handleToggleDanger}>
                  <WarningIcon/> Опасный чат
                </MenuItem>
              )}

              {/* Пункт меню для выхода из чата (для участников) */}
              {chat.participants?.length > 1 && !chat?.is_stranger && account && (
                <MenuItem 
                  startDecorator={<LogoutIcon />}
                  color="danger"
                  onClick={handleLeaveChat}
                  disabled={isLeaving}
                >
                  Покинуть чат
                </MenuItem>
              )}
            </Menu>
          </Dropdown>
        </Stack>
      )}
        {onCloseChat && (
          <Button size="sm" variant="plain" color="neutral"
            startDecorator={<CloseRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={onCloseChat} aria-label="Закрыть чат"
            sx={{ borderRadius: '10px', whiteSpace: 'nowrap' }}>
            Закрыть
          </Button>
        )}
      </Stack>
    </Stack>
  );
}



function AddContactDropdown({ chat, open, onClose, setAddContactOpen, onUserAdded }) {
  const { token } = useContext(LayoutContext);
  const [profiles, setProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && chat) {
      loadProfiles();
    }
  }, [open, chat]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await APIGetProfilesNotInChat(chat.chat_id, token);
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Ошибка загрузки сотрудников:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (profileId) => {
    try {
      setLoading(true);
      const response = await APIAddUserToChat(chat.chat_id, profileId, token);
      
      if (response.status === "Пользователь успешно добавлен в чат") {
        onUserAdded();
        onClose();
      }
    } catch (error) {
      console.error('Ошибка добавления пользователя:', error);
      alert('Ошибка при добавлении пользователя в чат');
    } finally {
      setLoading(false);
    }
  };

  function GetPosition(position) {
    if (position?.includes('manager')) return "Менеджер";
    if (position?.includes('surveyor')) return "Обмерщик";
    if (position?.includes('draftsman')) return "Чертежник";
    return "-";
  }

  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (Array.isArray(profile.position) && 
    profile.position.some(position => 
      position.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  return (
    <Tooltip
      placement="top-end"
      variant="outlined"
      arrow
      open={open}
      title={
        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2, boxShadow: 'lg' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography level="h4">Добавить контакт</Typography>
              <IconButton size="sm" onClick={onClose}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>

            <Input
              placeholder="Поиск сотрудников..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Typography>Загрузка...</Typography>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredProfiles.map((profile) => (
                  <ListItem key={profile.id}>
                    <ListItemButton
                      onClick={() => handleAddUser(profile.id)}
                      disabled={loading}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <AvatarGroup size="sm" />
                      <Box>
                        <Typography level="body-sm" fontWeight="bold">
                          {profile.name}
                        </Typography>
                        <Typography level="body-xs" color="primary">
                          {GetPosition(profile.position)}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {filteredProfiles.length === 0 && !loading && (
              <Typography level="body-sm" sx={{ textAlign: 'center', py: 2 }}>
                Нет доступных сотрудников для добавления
              </Typography>
            )}
          </Sheet>
      }
    >
      <IconButton 
        size="sm" 
        variant="outlined" 
        color="neutral"
        onClick={() => setAddContactOpen(!open)}
      >
        <PersonAddAltRoundedIcon />
      </IconButton>
    </Tooltip>
  );
}